import type { TipoColisao } from '../../dominio/didatico/timelineCinematica'
import {
  DADOS_IMPACTO_LATERAL,
  DADOS_SMALL_OVERLAP,
  TRAJETORIAS_FRONTAIS,
  type LadoImpactoLateral,
  type TrajetoriaFrontal,
} from '../../dominio/didatico/variacoesCinematica'
import { IconeAlerta, IconeOcupantes, IconeRisco } from '../icones'

interface Props {
  tipoColisao: TipoColisao
  comCinto: boolean
  trajetoriaFrontal: TrajetoriaFrontal
  smallOverlap: boolean
  ladoLateral: LadoImpactoLateral
  aoAlterarTrajetoriaFrontal: (t: TrajetoriaFrontal) => void
  aoAlternarSmallOverlap: (ativo: boolean) => void
  aoAlterarLadoLateral: (lado: LadoImpactoLateral) => void
  aoAplicarPresetMulticorpo: (preset: 'projetil' | 'cranio' | 'ejecaoParcial') => void
  /** Pilar 5.2: carroceria blindada, sem zona de deformação programada */
  blindado?: boolean
  aoAlternarBlindado?: (ativo: boolean) => void
}

/**
 * Seletor de variações de impacto realistas (Fase 3) e dinâmicas multicorpos (Fase 4).
 */
export function SeletorVariacoesCinematica({
  tipoColisao,
  comCinto,
  trajetoriaFrontal,
  smallOverlap,
  ladoLateral,
  aoAlterarTrajetoriaFrontal,
  aoAlternarSmallOverlap,
  aoAlterarLadoLateral,
  aoAplicarPresetMulticorpo,
  blindado = false,
  aoAlternarBlindado,
}: Props) {
  const ehFrontal = tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira'
  const ehLateral = tipoColisao === 'lateral' || tipoColisao === 'lateralComCapotamento'
  const ehRolagem = tipoColisao === 'capotamento' || tipoColisao === 'tombamento'

  return (
    <div className="seletor-variacoes-cinematica">
      {/* Variação Frontal: Up-and-Over vs Down-and-Under */}
      {ehFrontal && !comCinto && (
        <div className="grupo-controle">
          <span className="titulo-controle">Trajetória PHTLS (Sem Cinto)</span>
          <div className="grade-botoes-variacao">
            <button
              type="button"
              className={trajetoriaFrontal === 'upAndOver' ? 'ativo' : ''}
              onClick={() => aoAlterarTrajetoriaFrontal('upAndOver')}
            >
              Up-and-Over (Por Cima)
            </button>
            <button
              type="button"
              className={trajetoriaFrontal === 'downAndUnder' ? 'ativo' : ''}
              onClick={() => aoAlterarTrajetoriaFrontal('downAndUnder')}
            >
              Down-and-Under (Por Baixo)
            </button>
          </div>
          <p className="dica">
            {TRAJETORIAS_FRONTAIS[trajetoriaFrontal].descricao}
          </p>
        </div>
      )}

      {/* Variação Frontal: Small Overlap */}
      {ehFrontal && (
        <div className="grupo-controle">
          <span className="titulo-controle">Padrão de Sobreposição</span>
          <label className="toggle">
            <input
              type="checkbox"
              checked={smallOverlap}
              onChange={(e) => aoAlternarSmallOverlap(e.target.checked)}
            />
            Impacto com Pequena Sobreposição (Small Overlap 25%)
          </label>
          {smallOverlap && (
            <div className="alerta-small-overlap">
              <strong>{DADOS_SMALL_OVERLAP.nome}</strong>
              <p>{DADOS_SMALL_OVERLAP.mecanismo}</p>
              <small><strong>Implicação de Resgate:</strong> {DADOS_SMALL_OVERLAP.implicacaoSalvamento}</small>
            </div>
          )}
        </div>
      )}

      {/* Variação Lateral: Near-Side vs Far-Side */}
      {ehLateral && (
        <div className="grupo-controle">
          <span className="titulo-controle">Posição no Impacto Lateral</span>
          <div className="grade-botoes-variacao">
            <button
              type="button"
              className={ladoLateral === 'nearSide' ? 'ativo' : ''}
              onClick={() => aoAlterarLadoLateral('nearSide')}
            >
              Lado Próximo (Near-Side)
            </button>
            <button
              type="button"
              className={ladoLateral === 'farSide' ? 'ativo' : ''}
              onClick={() => aoAlterarLadoLateral('farSide')}
            >
              Lado Oposto (Far-Side)
            </button>
          </div>
          <p className="dica">{DADOS_IMPACTO_LATERAL[ladoLateral].descricao}</p>
        </div>
      )}

      {/* Tecnologia veicular: blindagem (Pilar 5.2) */}
      {aoAlternarBlindado && (
        <div className="grupo-controle">
          <span className="titulo-controle">Tecnologia Veicular</span>
          <label className="toggle">
            <input
              type="checkbox"
              checked={blindado}
              onChange={(e) => aoAlternarBlindado(e.target.checked)}
            />
            Veículo blindado
          </label>
          {blindado && (
            <p className="dica">
              A blindagem altera a massa e o comportamento estrutural. O efeito no ocupante depende
              do tipo de colisão e dos sistemas de retenção; examine o mecanismo e a vítima.
            </p>
          )}
        </div>
      )}

      {/* Presets didáticos de Dinâmica Multicorpos (Fase 4) */}
      <div className="grupo-controle">
        <span className="titulo-controle">Cenários Didáticos de Dinâmica Multicorpos</span>
        <div className="grade-botoes-presets-multicorpo">
          <button
            type="button"
            className="btn-preset-didatico"
            onClick={() => aoAplicarPresetMulticorpo('projetil')}
            title="Configura motorista de cinto e passageiro traseiro sem cinto como projétil"
          >
            <IconeOcupantes /> Ocupante Traseiro Projétil
          </button>
          <button
            type="button"
            className="btn-preset-didatico"
            onClick={() => aoAplicarPresetMulticorpo('cranio')}
            title="Configura motorista e carona dianteiro para colisão lateral com choque crânio-craniano"
          >
            <IconeRisco /> Choque Crânio-Craniano
          </button>
          {ehRolagem && (
            <button
              type="button"
              className="btn-preset-didatico"
              onClick={() => aoAplicarPresetMulticorpo('ejecaoParcial')}
              title="Demonstra projeção centrífuga e esmagamento de membros no solo"
            >
              <IconeAlerta /> Ejeção Parcial de Membro
            </button>
          )}
        </div>
        <p className="dica">
          Os presets multicorpos aplicam instantaneamente configurações de ocupantes e retenções
          destacadas nas diretrizes CBMMG e PHTLS.
        </p>
      </div>
    </div>
  )
}
